import { useMemo, useState } from "react";
import { useTran } from "@/hooks";
import { useAuthStore } from "@/features/auth";
import TourModal from "../components/TourModal";
import SolutionCard from "../components/SolutionCard";
import WeatherCard from "../components/WeatherCard";

const resolveGreeting = (t) => {
  const hour = new Date().getHours();
  if (hour < 12) return t("Good morning");
  if (hour < 18) return t("Good afternoon");
  return t("Good evening");
};

const MoreSolutionsPage = () => {
  const { t } = useTran();
  const { user } = useAuthStore();
  const [activeSolution, setActiveSolution] = useState(null);

  const greeting = resolveGreeting(t);
  const displayName =
    user?.displayName ||
    user?.name ||
    user?.username ||
    user?.email ||
    t("there");

  const solutions = useMemo(
    () => [
      {
        id: "nxmap",
        title: t("Carbon Manager - NXMap"),
        description: t(
          "Track and manage carbon emissions, energy, water, and waste data.",
        ),
        bullets: [
          t("Track and manage carbon emissions, energy, water, and waste data"),
        ],
        image: "/images/ghg.png",
        status: "active",
        ctaLabel: t("Take a Tour"),
        ctaVariant: "default",
        disabled: false,
      },
      {
        id: "nxplan",
        title: t("Emissions Reduction Solutions - NXPlan"),
        description: t(
          "Identify reduction opportunities across operations and supply chains.",
        ),
        bullets: [t("Identify reduction opportunities across operations")],
        image: "/images/cnp.jpg",
        status: "active",
        ctaLabel: t("Take a Tour"),
        ctaVariant: "default",
        disabled: false,
      },
      {
        id: "esg",
        title: t("Sustainability Report"),
        description: t("Create ESG and sustainability reports with ease."),
        bullets: [t("Create ESG and sustainability reports with ease")],
        image: "/images/esg.jpg",
        status: "active",
        ctaLabel: t("Take a Tour"),
        ctaVariant: "default",
        disabled: false,
      },
      {
        id: "nxops",
        title: t("Data and System Integration - NXOps"),
        description: t("Deliver next-generation operational performance data."),
        bullets: [
          t("Focuses on delivering next-generation operational performance."),
        ],
        image: "/images/under_construction.png",
        status: "under_construction",
        ctaLabel: t("Under Construction"),
        ctaVariant: "outline",
        disabled: true,
      },
      {
        id: "pcaf",
        title: t("PCAF & Financed Emissions Portfolio Management"),
        description: t(
          "Turn financed emissions into actionable portfolio insights.",
        ),
        bullets: [
          t(
            "PCAF: Turning financed emissions into actionable portfolio insights.",
          ),
        ],
        image: "/images/under_construction.png",
        status: "under_construction",
        ctaLabel: t("Under Construction"),
        ctaVariant: "outline",
        disabled: true,
      },
    ],
    [t],
  );

  const handleOpenTour = (solution) => {
    if (!solution || solution.status === "under_construction") return;
    setActiveSolution(solution);
  };

  return (
    <div className="space-y-6">
      <section className="overflow-hidden rounded-radius border border-border bg-gradient-to-tl from-secondary to-primary dark:from-primary/40 dark:to-card px-6 py-6 shadow-sm">
        <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
          <div>
            <p className="text-xl font-semibold text-white">
              {greeting}, {displayName}!
            </p>
            <p className="mt-2 max-w-2xl text-sm text-white/70">
              {t(
                "With our integrated tools, you can measure, manage, and reduce emissions while staying aligned with global standards. Together, these tools give you the clarity and confidence to achieve your net-zero goals.",
              )}
            </p>
          </div>
          <WeatherCard />
        </div>
      </section>

      <section className="grid gap-5 md:grid-cols-2 xl:grid-cols-4">
        {solutions.map((solution) => (
          <SolutionCard
            key={solution.title}
            solution={solution}
            onOpen={handleOpenTour}
          />
        ))}
      </section>

      <TourModal
        solution={activeSolution}
        onClose={() => setActiveSolution(null)}
      />
    </div>
  );
};

export default MoreSolutionsPage;
